<template>
  <div class="tool-inspector">
    <!-- Empty state -->
    <div v-if="!tool" class="empty-state">
      <Card class="p-8">
        <p class="text-gray-500 dark:text-gray-400 text-center">
          Select a tool from the list to inspect and execute it.
        </p>
      </Card>
    </div>

    <!-- Tool details -->
    <div v-else class="space-y-4 relative">
      <!-- Loading overlay -->
      <div
        v-if="isExecuting"
        class="absolute inset-0 bg-white/60 dark:bg-gray-900/60 z-50 flex flex-col items-center justify-center rounded-lg backdrop-blur-[2px]"
      >
        <LoadingSpinner size="xl" color="primary" text="Executing tool..." centered />
      </div>

      <!-- Tool header -->
      <Card>
        <div class="p-4">
          <h2 class="text-xl font-bold text-gray-900 dark:text-gray-100 mb-2">
            {{ tool.name }}
          </h2>
          <p class="text-sm text-gray-600 dark:text-gray-400">
            {{ tool.description }}
          </p>
        </div>
      </Card>

      <!-- Parameters form -->
      <Card>
        <div class="p-4">
          <h3 class="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">Parameters</h3>

          <!-- No parameters message -->
          <div v-if="!hasParameters" class="text-sm text-gray-500 dark:text-gray-400 italic">
            This tool has no parameters.
          </div>

          <!-- Parameter inputs -->
          <div v-else class="space-y-4">
            <SchemaField
              v-for="(paramSchema, paramName) in tool.inputSchema.properties"
              :key="paramName"
              :name="paramName"
              :schema="paramSchema"
              :model-value="parameters[paramName]"
              :required="isRequired(paramName)"
              :path="paramName"
              :errors="validationErrors"
              :suggestions="suggestions"
              :touched="touchedPaths"
              @update="updateParameter"
            />
          </div>

          <!-- Validation errors -->
          <Alert v-if="hasValidationErrors" variant="error" class="mt-4">
            <p class="text-sm">Please fix the validation errors before executing.</p>
          </Alert>

          <!-- Execution error -->
          <Alert v-if="executionError" variant="error" class="mt-4">
            <p class="text-sm font-semibold mb-1">Execution Error</p>
            <p class="text-sm">{{ executionError }}</p>
          </Alert>

          <!-- Execute button -->
          <div class="mt-6 flex gap-2">
            <Button
              @click="handleExecute"
              :disabled="isExecuting || hasValidationErrors"
              class="flex-1"
            >
              <LoadingSpinner v-if="isExecuting" class="mr-2 h-4 w-4" />
              {{ isExecuting ? 'Executing...' : 'Execute Tool' }}
            </Button>
            <Button variant="outline" @click="resetParameters" :disabled="isExecuting">
              Reset
            </Button>
          </div>
        </div>
      </Card>

      <!-- Response viewer -->
      <div v-if="lastResponse">
        <div class="flex items-center justify-between mb-2">
          <h3 class="text-lg font-semibold text-gray-900 dark:text-gray-100">Result</h3>
          <Badge v-if="lastResponse.duration" variant="info" class="text-xs">
            {{ (lastResponse.duration / 1000).toFixed(2) }}s
          </Badge>
        </div>
        <ResponseViewer :response="lastResponse" />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
  import { ref, computed, watch } from 'vue';
  import { Card, Button, Alert, LoadingSpinner, Badge } from '@asabdev/asab-ui';
  import SchemaField from './SchemaField.vue';
  import ResponseViewer from './ResponseViewer.vue';
  import type { McpTool, ToolResponse } from '../../types';
  import { useAuthStore } from '@/stores/auth';
  import { useOrganizationsStore } from '@/stores/organizations';
import { toUserMessage } from '@/lib/api/errors';
  import {
    autoPopulateParameters,
    deepClone,
    setValueAtPath,
    validateParameters,
  } from '../../lib/mcp-schema';

  interface Props {
    tool: McpTool | null;
  }

  interface Emits {
    (e: 'execute', parameters: Record<string, unknown>): void;
  }

  const props = defineProps<Props>();
  const emit = defineEmits<Emits>();

  const parameters = ref<Record<string, unknown>>({});
  const validationErrors = ref<Map<string, string>>(new Map());
  const lastResponse = ref<ToolResponse | null>(null);
  const isExecuting = ref(false);
  const executionError = ref<string | null>(null);
  const touchedPaths = ref<Set<string>>(new Set());
  const suggestions = ref<Map<string, unknown>>(new Map());

  const authStore = useAuthStore();
  const orgStore = useOrganizationsStore();

  const contextData = computed(() => {
    return {
      user_id: authStore.user?.id,
      userId: authStore.user?.id,
      email: authStore.user?.email,
      user_email: authStore.user?.email,
      name: authStore.user?.name || authStore.user?.full_name,
      full_name: authStore.user?.full_name,
      organization_id: orgStore.currentOrganizationId,
      org_id: orgStore.currentOrganizationId,
      orgId: orgStore.currentOrganizationId,
      token: authStore.token,
    };
  });

  const hasParameters = computed(() => {
    return (
      props.tool?.inputSchema.properties &&
      Object.keys(props.tool.inputSchema.properties).length > 0
    );
  });

  const hasValidationErrors = computed(() => {
    return validationErrors.value.size > 0;
  });

  const isRequired = (paramName: string): boolean => {
    return props.tool?.inputSchema.required?.includes(paramName) ?? false;
  };

  const runValidation = (): boolean => {
    if (!props.tool) return false;
    const errors = validateParameters(props.tool.inputSchema, parameters.value);
    validationErrors.value = errors;
    return errors.size === 0;
  };

  const updateParameter = (path: string, value: unknown, userInitiated: boolean) => {
    const next = deepClone(parameters.value) as Record<string, unknown>;
    setValueAtPath(next, path, value);
    parameters.value = next;
    if (userInitiated) {
      touchedPaths.value = new Set(touchedPaths.value).add(path);
    }
    runValidation();
  };

  const applyAutoPopulate = () => {
    if (!props.tool) return;
    const result = autoPopulateParameters(props.tool.inputSchema, {
      context: contextData.value,
      existing: parameters.value,
      touchedPaths: touchedPaths.value,
    });
    parameters.value = result.values;
    suggestions.value = result.suggestions;
    runValidation();
  };

  const handleExecute = async () => {
    if (!runValidation()) {
      return;
    }

    isExecuting.value = true;
    executionError.value = null;
    lastResponse.value = null;

    try {
      // Emit execute event with parameters
      emit('execute', { ...parameters.value });

      // Note: The parent component will handle the actual execution
      // and should call setResponse() with the result
    } catch (error) {
      executionError.value = toUserMessage(error, 'Unknown error occurred');
      isExecuting.value = false;
    }
  };

  const resetParameters = () => {
    parameters.value = {};
    validationErrors.value = new Map();
    executionError.value = null;
    touchedPaths.value = new Set();
    applyAutoPopulate();
  };

  const setResponse = (response: ToolResponse) => {
    lastResponse.value = response;
    isExecuting.value = false;

    if (!response.success) {
      executionError.value = response.error?.message || 'Tool execution failed';
    } else {
      executionError.value = null;
    }
  };

  const setExecuting = (executing: boolean) => {
    isExecuting.value = executing;
  };

  // Reset when tool changes
  watch(
    () => props.tool,
    newTool => {
      if (newTool) {
        resetParameters();
        lastResponse.value = null;
      }
    },
    { immediate: true }
  );

  watch(
    () => contextData.value,
    () => {
      applyAutoPopulate();
    },
    { deep: true }
  );

  // Expose methods for parent component
  defineExpose({
    setResponse,
    setExecuting,
  });
</script>

<style scoped>
  .tool-inspector {
    @apply w-full;
  }

  .empty-state {
    @apply flex items-center justify-center min-h-[400px];
  }
</style>
